import { useState, useEffect, useRef } from "react"
import { Bookmark, ChevronLeft, ChevronRight, Heart, Copy, ArrowUpRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { fetchFavoriteList } from "@/lib/community/community-api"
import { ABILITY_TYPE_CONFIG } from "@/lib/community/types"
import type { CommunityPost } from "@/lib/community/types"

function FavoriteCard({
  post,
  onClick,
}: {
  post: CommunityPost
  onClick: () => void
}) {
  const config = ABILITY_TYPE_CONFIG[post.abilityType]

  return (
    <article
      onClick={onClick}
      className="profile-favorite-card group shrink-0 w-[280px] md:w-[320px] rounded-xl liquid-glass-card overflow-hidden cursor-pointer transition-all duration-300 hover:border-primary/50 hover:-translate-y-1"
    >
      {/* Thumbnail */}
      <div className="relative w-full aspect-[16/10] overflow-hidden bg-secondary">
        {post.coverImage ? (
          <img
            src={post.coverImage}
            alt={post.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-secondary/50">
            <span className={cn("text-4xl opacity-20 font-mono font-bold", config.outlineTextClass)}>
              {config.label}
            </span>
          </div>
        )}

        {/* Type tag */}
        <div className={cn(
          "absolute top-3 left-3 px-2.5 py-1 rounded-full text-micro font-medium tracking-wide backdrop-blur-sm bg-black/30 text-white",
        )}>
          {config.label}
        </div>

        {/* Hover arrow */}
        <div className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-primary flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          <ArrowUpRight className="w-4 h-4 text-primary-foreground" />
        </div>
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col gap-2">
        <h4 className="font-medium text-sm leading-snug line-clamp-2 group-hover:text-primary transition-colors">
          {post.title}
        </h4>
        <div className="flex items-center gap-3 text-muted-foreground text-xs">
          <span className="flex items-center gap-1">
            <Heart className="w-3 h-3" />
            {post.stats.likes.toLocaleString()}
          </span>
          <span className="flex items-center gap-1">
            <Copy className="w-3 h-3" />
            {post.stats.copies.toLocaleString()}
          </span>
        </div>
      </div>
    </article>
  )
}

interface ProfileFavoritesSectionProps {
  isOwnProfile: boolean
  onPostClick: (post: CommunityPost) => void
}

export function ProfileFavoritesSection({ isOwnProfile, onPostClick }: ProfileFavoritesSectionProps) {
  const [posts, setPosts] = useState<CommunityPost[]>([])
  const [loading, setLoading] = useState(true)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchFavoriteList(1, 20).then((res) => {
      if (!cancelled) {
        setPosts(res.posts)
        setLoading(false)
      }
    }).catch(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  const updateScrollState = () => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 10)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10)
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener("scroll", updateScrollState, { passive: true })
    const ro = new ResizeObserver(updateScrollState)
    ro.observe(el)
    return () => {
      el.removeEventListener("scroll", updateScrollState)
      ro.disconnect()
    }
  }, [posts])

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current
    if (!el) return
    const distance = el.clientWidth * 0.7
    el.scrollBy({ left: direction === "left" ? -distance : distance, behavior: "smooth" })
  }

  // Don't show section for non-own profiles
  if (!isOwnProfile) return null

  return (
    <section className="profile-section">
      <div>
        {/* Header */}
        <div className="profile-section-header">
          <h2 className="profile-section-title">我的收藏</h2>
          {posts.length > 3 && (
            <div className="hidden md:flex items-center gap-2">
              <button
                type="button"
                onClick={() => scroll("left")}
                disabled={!canScrollLeft}
                className="profile-section-icon-action"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => scroll("right")}
                disabled={!canScrollRight}
                className="profile-section-icon-action"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Horizontal scroll — full bleed */}
      {loading ? (
        <div>
          <div className="flex gap-4 overflow-hidden">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="shrink-0 w-[280px] md:w-[320px] rounded-xl bg-muted/30 animate-pulse aspect-[4/3]" />
            ))}
          </div>
        </div>
      ) : posts.length === 0 ? (
        <div className="profile-empty-state">
          <Bookmark className="h-10 w-10 profile-empty-state-icon" />
          <p className="profile-empty-state-title">还没有收藏内容</p>
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide pb-2"
        >
          {posts.map((post) => (
            <FavoriteCard
              key={post.id}
              post={post}
              onClick={() => onPostClick(post)}
            />
          ))}
        </div>
      )}
    </section>
  )
}
